import { useState } from "react"
import { onContractPaymentService } from "../../services/onContractPaymentService"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import { X, IndianRupee } from "lucide-react"

const PAYMENT_MODES = ["Cash", "Bank Transfer", "Cheque", "UPI", "NEFT", "RTGS", "Other"]

const AddPaymentModal = ({ contractId, balanceAmount = 0, onClose, onSuccess }) => {
  const { user } = useAuth()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    amount: "",
    payment_date: new Date().toISOString().split("T")[0],
    payment_mode: "",
    reference_number: "",
    remarks: "",
  })
  const [errors, setErrors] = useState({})

  const validate = () => {
    const e = {}
    if (!form.amount || isNaN(form.amount) || parseFloat(form.amount) <= 0)
      e.amount = "Enter a valid amount greater than 0"
    else if (parseFloat(form.amount) > balanceAmount)
      e.amount = `Amount cannot exceed balance due of ₹${balanceAmount.toFixed(2)}`
    if (!form.payment_date) e.payment_date = "Payment date is required"
    return e
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === "amount" && value !== "" && parseFloat(value) > balanceAmount) {
      setErrors({ ...errors, amount: `Amount cannot exceed balance due of ₹${balanceAmount.toFixed(2)}` })
      setForm({ ...form, [name]: value })
      return
    }
    setForm({ ...form, [name]: value })
    if (errors[name]) setErrors({ ...errors, [name]: "" })
  }

  const handleSubmit = async () => {
    const e = validate()
    if (Object.keys(e).length) { setErrors(e); return }
    try {
      setSaving(true)
      await onContractPaymentService.create(contractId, {
        ...form,
        amount: parseFloat(form.amount),
        created_by: user?.user_id,
      })
      Alert.success("Payment recorded successfully")
      onSuccess()
      onClose()
    } catch (err) {
      Alert.error(err.response?.data?.message || "Failed to save payment")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-3">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <IndianRupee className="w-4 h-4 text-blue-500" />
            <h2 className="font-bold text-secondary text-base">Add Payment</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Amount */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Amount (₹) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="amount"
              value={form.amount}
              onChange={handleChange}
              placeholder="0.00"
              min="0"
              max={balanceAmount}
              className={`w-full px-3 py-2 border rounded-sm text-sm focus:outline-none focus:border-brand ${
                errors.amount ? "border-red-400" : "border-gray-300"
              }`}
            />
            {errors.amount && <p className="text-xs text-red-500 mt-1">{errors.amount}</p>}
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Payment Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="payment_date"
              value={form.payment_date}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-sm text-sm focus:outline-none focus:border-brand ${
                errors.payment_date ? "border-red-400" : "border-gray-300"
              }`}
            />
            {errors.payment_date && <p className="text-xs text-red-500 mt-1">{errors.payment_date}</p>}
          </div>

          {/* Payment Mode */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Payment Mode</label>
            <select
              name="payment_mode"
              value={form.payment_mode}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:border-brand"
            >
              <option value="">Select mode</option>
              {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          {/* Reference Number */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Reference / Cheque No.</label>
            <input
              type="text"
              name="reference_number"
              value={form.reference_number}
              onChange={handleChange}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:border-brand"
            />
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Remarks</label>
            <textarea
              name="remarks"
              value={form.remarks}
              onChange={handleChange}
              rows={2}
              placeholder="Optional notes"
              className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm focus:outline-none focus:border-brand resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-sm hover:bg-gray-50 font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !!errors.amount || (form.amount !== "" && parseFloat(form.amount) > balanceAmount)}
            className="px-4 py-2 text-sm text-buttonTxt bg-buttonBg hover:text-buttonBg hover:bg-buttonTxt border border-current rounded-sm font-semibold transition-all disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Payment"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AddPaymentModal